import { useCallback, useEffect, useState } from 'react';
import { NumberParam, withDefault, useQueryParams } from 'use-query-params';
import devicesRepository from 'modules/devices/domain/repositories/devices-repository';
import { DeviceDto } from 'modules/core/supabase/tables/device';

const PER_PAGE_KEY = 'devicesPerPage';

const defaultFilters = {
  page: withDefault(NumberParam, 1),
  perPage: withDefault(NumberParam, Number(localStorage.getItem(PER_PAGE_KEY)) || 9),
};

export function useDevicesList(tournamentId: number | null) {
  const [filters, setFilters] = useQueryParams(defaultFilters);
  const page = filters.page ?? 1;
  const perPage = filters.perPage ?? 9;

  const [devices, setDevices] = useState<DeviceDto[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);
  const [refetchCounter, setRefetchCounter] = useState(0);

  const refetch = useCallback(() => setRefetchCounter((c) => c + 1), []);

  const setPage = useCallback(
    (p: number) => setFilters({ page: p }),
    [setFilters],
  );

  const setPerPage = useCallback(
    (v: number) => {
      localStorage.setItem(PER_PAGE_KEY, String(v));
      setFilters({ perPage: v, page: 1 });
    },
    [setFilters],
  );

  useEffect(() => {
    if (!tournamentId) return;
    setIsLoading(true);
    setIsError(false);
    devicesRepository
      .list({
        tournament_id: tournamentId,
        offset: (page - 1) * perPage,
        limit: perPage,
      })
      .then((data) => {
        setDevices(data);
        setIsLoading(false);
      })
      .catch(() => {
        setIsError(true);
        setIsLoading(false);
      });
  }, [tournamentId, page, perPage, refetchCounter]);

  const pages = devices.length < perPage ? page : page + 1;

  return { devices, isLoading, isError, page, perPage, pages, setPage, setPerPage, refetch };
}
